
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSubscription } from '../hooks/useSubscription';
import PlanGrid from '../components/subscription/PlanGrid';
import CheckoutModal from '../components/subscription/CheckoutModal';
import PayPerVideoPricing from '../components/pricing/PayPerVideoPricing';
import { loadStripe } from '@stripe/stripe-js';

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { plans, loading, error, loadPlans } = useSubscription();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [stripe, setStripe] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    const initStripe = async () => {
      try {
        const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        if (publishableKey) {
          const stripeInstance = await loadStripe(publishableKey);
          setStripe(stripeInstance);
        }
      } finally {
        setStripeLoading(false);
      }
    };
    initStripe();
  }, []);

  const handlePlanSelect = (plan) => {
    if (!user) {
      navigate('/login', { state: { from: location.pathname || '/pricing' } });
      return;
    }
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  const handleCheckoutSuccess = (result) => {
    setShowCheckout(false);
    setSelectedPlan(null);
    navigate('/account/subscription', {
      state: {
        message:
          result.type === 'free'
            ? 'Free subscription started successfully!'
            : 'Payment successful! Your subscription is now active.',
      },
    });
  };

  const handleCheckoutClose = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Top bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-6 lg:px-8 pt-4 sm:pt-5 md:pt-6">
        <div className="flex items-center justify-end">
          {!user && (
            <button
              onClick={() => navigate('/login')}
              className="text-sm sm:text-base text-brand-red font-semibold hover:text-red-700 transition-colors px-2 py-1"
            >
              Login
            </button>
          )}
        </div>
      </div>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-5 md:px-6 lg:px-8 py-6 sm:py-8 md:py-10 lg:py-12">
        {/* Hero */}
        <div className="text-center mb-6 sm:mb-8 md:mb-10 lg:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3">
            Choose how you want to pay
          </h1>
          <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto px-2">
            You can{" "}
            <span className="font-semibold text-gray-900">pay per video with no subscription</span>{" "}
            or{" "}
            <span className="font-semibold text-gray-900">add a subscription</span> for extra storage
            and free monthly video credits.
          </p>
        </div>

        {/* Pay-per-video explanation (always on top) */}
        <section className="max-w-3xl mx-auto mb-6 sm:mb-8 md:mb-10">
          <div className="mb-3 sm:mb-4 text-center">
            <p className="text-xs sm:text-sm md:text-base text-gray-700">
              <span className="font-semibold text-gray-900">Option 1:</span>{" "}
              Pay only when you need a video processed. No subscription required.
            </p>
          </div>
          <PayPerVideoPricing />
        </section>

        {/* Subscription plans below pay-per-video */}
        <section className="max-w-6xl mx-auto">
          <div className="mb-4 sm:mb-5 md:mb-6 text-center">
            <p className="text-xs sm:text-sm md:text-base text-gray-700">
              <span className="font-semibold text-gray-900">Option 2:</span>{" "}
              Choose a subscription for teams that need{" "}
              <span className="font-semibold">more storage</span> and{" "}
              <span className="font-semibold">free monthly video credits</span>.
            </p>
          </div>

          {/* PlanGrid - Subscription Plans */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 p-4 sm:p-5 md:p-6 lg:p-7 xl:p-8 overflow-hidden">
            <PlanGrid
              plans={plans}
              onPlanSelect={handlePlanSelect}
              loading={loading}
              error={error}
            />
          </div>
        </section>
      </main>

      {/* Checkout */}
      {selectedPlan && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={handleCheckoutClose}
          plan={selectedPlan}
          stripe={stripe}
          onSuccess={handleCheckoutSuccess}
          onError={() => {}}
          actionType="new"
        />
      )}

      {/* Stripe Loading */}
      {stripeLoading && (
        <div className="fixed inset-0 z-50 bg-white/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="text-center bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-5 w-full max-w-xs">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-700 text-sm">Initializing payment system...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
